import { useEffect, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useMatch, useParams, useSearchParams } from 'react-router-dom';
import { useDb, useMe, sendPresence } from '@/lib/store';
import { useUi } from '@/lib/ui';
import { TopBar } from './TopBar';
import { Rail, useRail } from './Rail';
import { Sidebar } from './Sidebar';
import { DialogHost } from './dialogs';
import { LocationPage } from '@/pages/LocationPage';
import { Placeholder } from '@/pages/Placeholder';
import { TaskModal } from '@/task/TaskModal';
import { CommandPalette } from '@/command/CommandPalette';
import { CreateTaskModal } from '@/task/CreateTaskModal';
import { useGlobalShortcuts } from './shortcuts';
import { useDesktopIntegration } from '@/lib/desktop';
import { NotificationPrompt } from './NotificationPrompt';
import { FieldEditorHost } from '@/views/FieldsPanel';
import { useIsMobile } from '@/mobile/useIsMobile';
import { MobileShell } from '@/mobile/MobileShell';

const HomePage = lazy(() => import('@/pages/Home').then((m) => ({ default: m.HomePage })));
const InboxPage = lazy(() => import('@/pages/Inbox').then((m) => ({ default: m.InboxPage })));
const RepliesPage = lazy(() => import('@/pages/Inbox').then((m) => ({ default: m.RepliesPage })));
const AssignedCommentsPage = lazy(() => import('@/pages/Inbox').then((m) => ({ default: m.AssignedCommentsPage })));
const ChatPage = lazy(() => import('@/pages/Chat').then((m) => ({ default: m.ChatPage })));
const DocsPage = lazy(() => import('@/pages/Docs').then((m) => ({ default: m.DocsPage })));
const PlannerPage = lazy(() => import('@/pages/Planner').then((m) => ({ default: m.PlannerPage })));
const TeamsPage = lazy(() => import('@/pages/Teams').then((m) => ({ default: m.TeamsPage })));
const TimesheetsPage = lazy(() => import('@/pages/Timesheets').then((m) => ({ default: m.TimesheetsPage })));
const SettingsPage = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.SettingsPage })));

function LocationRoute({ type }: { type: 'list' | 'folder' | 'space' | 'workspace' }) {
  const { ws = '', id, viewId } = useParams();
  return <LocationPage key={`${type}:${id ?? ws}`} location={{ type, id: type === 'workspace' ? ws : (id ?? '') }} viewId={viewId} />;
}

function SectionSync() {
  // keep the rail highlight in sync with the current page
  const { pathname } = useLocation();
  useEffect(() => {
    const seg = pathname.split('/')[2] ?? '';
    const map: Record<string, Parameters<typeof useRail.setState>[0]> = {
      chat: { section: 'chat' },
      planner: { section: 'planner' },
      docs: { section: 'docs' },
      teams: { section: 'teams' },
      timesheets: { section: 'timesheets' },
      dashboards: { section: 'dashboards' },
      whiteboards: { section: 'whiteboards' },
      forms: { section: 'forms' },
      clips: { section: 'clips' },
      goals: { section: 'goals' },
      inbox: { section: 'home' },
      home: { section: 'home' },
    };
    const next = map[seg];
    if (next) useRail.setState(next);
    sendPresence(pathname);
  }, [pathname]);
  return null;
}

export function AppShell() {
  const { ws = '' } = useParams();
  const me = useMe();
  const workspaces = useDb((db) => db.workspaces);
  const workspace = workspaces[ws];
  const [params] = useSearchParams();
  const taskId = params.get('t');
  const commandOpen = useUi((s) => s.commandOpen);
  const createOpen = useUi((s) => s.createTaskOpen);
  const sidebarCollapsed = useUi((s) => s.sidebarCollapsed);
  // Settings has its own navigation, so the sidebar steps aside there (without changing the saved preference)
  const inSettings = useMatch('/:ws/settings') !== null;
  const hideSidebar = sidebarCollapsed || inSettings;
  // phones get their own layout: tab bar, full-screen pages and bottom sheets (mobile/)
  const mobile = useIsMobile();
  useGlobalShortcuts();
  useDesktopIntegration(ws);

  if (!workspace) {
    const first = Object.values(workspaces).find((w) => w.memberIds.includes(me));
    return first ? <Navigate to={`/${first.id}/home`} replace /> : <div className="boot">No workspace found.</div>;
  }
  if (mobile)
    return (
      <>
        <SectionSync />
        <MobileShell />
      </>
    );

  return (
    <div className="app">
      <SectionSync />
      <TopBar />
      <div className="app-body">
        <Rail />
        <div className="app-container">
          {/* stays mounted so collapsing/expanding can slide (shell.css .sidebar-slot) */}
          <div className={hideSidebar ? 'sidebar-slot collapsed' : 'sidebar-slot'} aria-hidden={hideSidebar || undefined}>
            <Sidebar />
          </div>
          <main className="main">
            <NotificationPrompt />
            <Suspense fallback={<div className="page-placeholder" />}>
              <Routes>
                <Route path="home" element={<HomePage />} />
                <Route path="my-tasks" element={<HomePage />} />
                <Route path="inbox" element={<InboxPage />} />
                <Route path="replies" element={<RepliesPage />} />
                <Route path="assigned-comments" element={<AssignedCommentsPage />} />
                <Route path="l/:id/:viewId?" element={<LocationRoute type="list" />} />
                <Route path="f/:id/:viewId?" element={<LocationRoute type="folder" />} />
                <Route path="s/:id/:viewId?" element={<LocationRoute type="space" />} />
                <Route path="everything/:viewId?" element={<LocationRoute type="workspace" />} />
                <Route path="chat/activity" element={<ChatPage view="activity" />} />
                <Route path="chat/:channelId?" element={<ChatPage />} />
                <Route path="docs/:docId?/:pageId?" element={<DocsPage />} />
                <Route path="planner" element={<PlannerPage />} />
                <Route path="teams" element={<TeamsPage />} />
                <Route path="timesheets" element={<TimesheetsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="dashboards" element={<Placeholder kind="dashboards" />} />
                <Route path="whiteboards" element={<Placeholder kind="whiteboards" />} />
                <Route path="forms" element={<Placeholder kind="forms" />} />
                <Route path="clips" element={<Placeholder kind="clips" />} />
                <Route path="goals" element={<Placeholder kind="goals" />} />
                <Route path="*" element={<Navigate to="home" replace />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
      {taskId && <TaskModal key={taskId} taskId={taskId} />}
      {commandOpen && <CommandPalette />}
      {createOpen && <CreateTaskModal />}
      <DialogHost />
      <FieldEditorHost />
    </div>
  );
}
